import type { GameBookImportRequest } from "../../../../shared/contracts/gamebook-import.js";
import {
  guestAssignment,
  validateReview,
  type ReviewState,
} from "./ggb-review.js";
import { importPlayedAt } from "./ggb-review-validation.js";
import { reviewedTotals } from "./ggb-totals-review.js";
import { stakeSnapshot } from "../wizard-state.js";

export function buildServerImportRequest(
  review: ReviewState,
  requestId: string,
): GameBookImportRequest {
  const error = validateReview(review, undefined, true);
  if (error) throw new Error(error);
  if (!review.format || !review.scoringBasis)
    throw new Error("Confirm the scoring agreement.");
  if (!review.courseId && (review.courseLocation?.trim().length ?? 0) < 2)
    throw new Error(
      "Choose a saved course or enter its town and country so rounds use the same course.",
    );
  if (new Set(review.sourceImageHashes).size > 20)
    throw new Error("Choose at most 20 different screenshots for one import.");
  return {
    requestId,
    ...(review.stake ? { stake: stakeSnapshot(review.stake) } : {}),
    ...(review.detail === "totals-only"
      ? {}
      : { completion: "review" as const }),
    ...(review.detail === "totals-only"
      ? { detail: "totals-only" as const, holeCount: review.holeCount! }
      : {}),
    sourceImageHashes: review.sourceImageHashes ?? [],
    course: review.courseId
      ? { kind: "existing", courseId: review.courseId }
      : {
          kind: "manual",
          name: review.course.trim(),
          location: review.courseLocation!.trim(),
        },
    playedAt: importPlayedAt(review),
    ...(review.playedTimeOfDay
      ? { playedTimeOfDay: review.playedTimeOfDay }
      : {}),
    ...(review.datePrecision && review.datePrecision !== "instant"
      ? { playedAtPrecision: review.datePrecision }
      : {}),
    format: review.format,
    scoringBasis: review.scoringBasis,
    orderedHoleNumbers:
      review.detail === "totals-only" ? [] : [...review.holes],
    confirmed: true,
    players: review.cards
      .filter((card) => card.assign)
      .map((card) => ({
        ...(review.conditions?.[card.key]?.length
          ? { tags: review.conditions[card.key] }
          : {}),
        identity:
          card.assign === guestAssignment
            ? { kind: "guest", displayName: card.name.trim() }
            : { kind: "participant", participantId: card.assign },
        teeName: card.teeName.trim(),
        ...(card.teeId ? { teeId: card.teeId } : {}),
        playingHandicap: card.playingHandicap.trim()
          ? Number(card.playingHandicap)
          : null,
        ...(review.detail === "totals-only"
          ? { totals: reviewedTotals(card) }
          : {}),
        holes: (review.detail === "totals-only" ? [] : review.holes).map(
          (number) => ({
            number,
            par: Number(card.par[number]),
            strokeIndex: Number(card.si[number]),
          }),
        ),
        scores: Object.fromEntries(
          (review.detail === "totals-only" ? [] : review.holes)
            .filter((hole) => card.scores[hole]?.trim())
            .map((hole) => [
              hole,
              card.scores[hole] === "P" ? null : Number(card.scores[hole]),
            ]),
        ),
      })),
  };
}

/** Only fingerprints leave the browser; images remain in the local OCR flow. */
export async function fingerprintImportImages(
  files: readonly Blob[],
): Promise<string[]> {
  const hashes = await Promise.all(
    files.map(async (file) => {
      const hash = await crypto.subtle.digest(
        "SHA-256",
        await file.arrayBuffer(),
      );
      return Array.from(new Uint8Array(hash), (byte) =>
        byte.toString(16).padStart(2, "0"),
      ).join("");
    }),
  );
  return [...new Set(hashes)].sort();
}
